import { useRef, useState } from "react";
import { AccountSecurityDialog } from "@/components/mfa/AccountSecurityDialog";
import { canAccessAdmin } from "@/lib/admin-access";
import { logout } from "@/lib/api";
import { formatFullName } from "@/lib/format-name";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { Avatar } from "./Avatar";
import { ContextMenu, type ContextMenuEntry } from "./ContextMenu";

interface AccountMenuProps {
	/** アバターのみを表示する。サイドバー折りたたみ時に使う。 */
	iconOnly?: boolean;
}

/** アバターから管理・多要素認証・ロードマップ・利用ガイド・ログアウトを開くアカウントメニュー。左パネル最下部に置く。 */
export function AccountMenu({ iconOnly = false }: AccountMenuProps) {
	const { firstName, lastName, role, roadmapAvailable } = useCurrentUserStore();
	const [menuOpen, setMenuOpen] = useState(false);
	const [securityOpen, setSecurityOpen] = useState(false);
	const [hovered, setHovered] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const menuItems: ContextMenuEntry[] = [];
	if (canAccessAdmin(role)) {
		menuItems.push({
			key: "admin",
			label: "管理",
			testId: "account-menu-item-admin",
			routerLink: "/admin",
		});
	}
	menuItems.push({
		key: "security",
		label: "多要素認証",
		testId: "account-menu-item-security",
		onSelect: () => setSecurityOpen(true),
	});
	if (roadmapAvailable) {
		menuItems.push({
			key: "roadmap",
			label: "ロードマップ",
			testId: "account-menu-item-roadmap",
			routerLink: "/roadmap",
		});
	}
	menuItems.push({
		key: "docs",
		label: "利用ガイド",
		testId: "account-menu-item-docs",
		routerLink: "/docs",
		openInNewTab: true,
	});
	menuItems.push({ separator: true });
	menuItems.push({
		key: "logout",
		label: "ログアウト",
		testId: "account-menu-item-logout",
		onSelect: () => logout(),
	});

	const fullName = firstName || lastName ? formatFullName({ firstName, lastName }) : "";

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				data-testid="account-menu-trigger"
				onClick={() => setMenuOpen((v) => !v)}
				onPointerEnter={() => setHovered(true)}
				onPointerLeave={() => setHovered(false)}
				title={iconOnly ? fullName || undefined : undefined}
				data-tooltip-side="right"
				data-tooltip-delay={0}
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					// アイコンのみでも幅いっぱいに広げる。中央寄せしたアバターの中心が
					// サイドバーのナビアイコンと同じ軸に乗る。
					flex: 1,
					minWidth: 0,
					height: 32,
					padding: iconOnly ? 0 : "0 6px",
					justifyContent: iconOnly ? "center" : "flex-start",
					border: 0,
					borderRadius: "var(--radius-3)",
					background: menuOpen || hovered ? "var(--overlay-hover)" : "transparent",
					color: menuOpen || hovered ? "var(--fg-0)" : "var(--fg-2)",
					cursor: "pointer",
					fontFamily: "inherit",
					fontSize: "var(--text-ui)",
					fontWeight: 500,
					letterSpacing: "var(--tracking-snug)",
					transition: "background 120ms ease, color 120ms ease",
				}}
			>
				<Avatar firstName={firstName} lastName={lastName} size={22} />
				{!iconOnly && fullName && (
					<span
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{fullName}
					</span>
				)}
			</button>
			{menuOpen && triggerRef.current && (
				<ContextMenu
					position={{ anchor: triggerRef.current, align: "left", offset: 8 }}
					onClose={() => setMenuOpen(false)}
					items={menuItems}
					testId="account-menu"
				/>
			)}
			<AccountSecurityDialog open={securityOpen} onClose={() => setSecurityOpen(false)} />
		</>
	);
}
